import { IonButton, IonContent } from '@ionic/react';
import { ActionFooter } from '@mobile/ui';
import { ErrorMessage, InfoIcon } from '@shared/ui';
import { PhotoGrid } from '@square-photo/components/photo-grid';
import type { PhotoFile, ProcessedPhoto } from '@square-photo/types';

export interface DoneScreenProps {
  photos: PhotoFile[];
  processedPhotos: ProcessedPhoto[];
  zipFilename: string;
  canShare: boolean;
  onShare: () => void;
  onDownloadZip: () => void;
  onReset: () => void;
}

/** 轉換完成的結果畫面：失敗清單、儲存說明與「重新開始／分享」操作。 */
export function DoneScreen({
  photos,
  processedPhotos,
  zipFilename,
  canShare,
  onShare,
  onDownloadZip,
  onReset,
}: DoneScreenProps) {
  const successCount = processedPhotos.filter(
    (p) => p.status === 'success',
  ).length;
  const errorCount = processedPhotos.filter((p) => p.status === 'error').length;

  /* 帶上原始檔名，使用者才知道是哪一張失敗 */
  const errorItems = processedPhotos
    .filter((p) => p.status === 'error' && p.error)
    .map((p) => {
      const photo = photos.find((ph) => ph.id === p.id);
      const name = photo?.file.name ?? p.id;
      return `${name} — ${p.error?.message ?? '未知錯誤'}`;
    });

  return (
    <>
      <IonContent>
        <div className="space-y-4 px-4 pt-4">
          {errorItems.length > 0 && (
            <ErrorMessage
              title={`${errorCount} 張照片轉換失敗`}
              items={errorItems}
            />
          )}

          {successCount > 0 && (
            <div className="rounded-xl border border-border bg-card px-4 py-3">
              <div className="flex items-start gap-3">
                <InfoIcon size={20} className="mt-0.5 shrink-0 text-accent" />
                <div>
                  {canShare ? (
                    <>
                      <p className="mb-1 text-sm text-foreground">
                        儲存至相簿
                      </p>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        點「分享到相簿」，在分享選單中選擇「儲存圖像」即可直接存入相簿。
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="mb-1 text-sm text-foreground">
                        下載說明
                      </p>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        所有照片已打包為 ZIP，點下載後儲存到裝置。
                      </p>
                      <p className="mt-2 font-mono text-xs text-muted-foreground">
                        {zipFilename}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 p-1 md:p-1.5">
          <PhotoGrid photos={photos} showRemoveButton={false} />
        </div>
      </IonContent>

      <ActionFooter>
        <IonButton fill="outline" onClick={onReset}>
          重新開始
        </IonButton>
        {successCount > 0 && (
          <IonButton
            onClick={canShare ? onShare : onDownloadZip}
            className="flex-1"
          >
            {canShare ? '分享到相簿' : '下載 ZIP'}
          </IonButton>
        )}
      </ActionFooter>
    </>
  );
}
